"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Shield, Key, Lock, User, Globe, Cloud, FileKey } from "lucide-react";
import { APIKeyForm } from "./auth-forms/api-key-form";
import { BearerForm } from "./auth-forms/bearer-form";
import { BasicAuthForm } from "./auth-forms/basic-auth-form";
import { OAuth2Form } from "./auth-forms/oauth2-form";
import { AWSSignatureForm } from "./auth-forms/aws-signature-form";
import { JWTForm } from "./auth-forms/jwt-form";
import { SMBConfigForm } from "./auth-forms/smb-config-form";
import { DatabaseConfigForm } from "./auth-forms/database-config-form";
import type { ConnectionConfig } from "../connection-studio";

interface AuthTabProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
}

const AUTH_TYPES = [
  {
    value: "none",
    label: "No Auth",
    icon: Globe,
    description: "Public API with no authentication",
  },
  {
    value: "api_key",
    label: "API Key",
    icon: Key,
    description: "API key in header or query parameter",
  },
  {
    value: "bearer_token",
    label: "Bearer Token",
    icon: Shield,
    description: "Bearer token in Authorization header",
  },
  {
    value: "basic_auth",
    label: "Basic Auth",
    icon: User,
    description: "Username and password authentication",
  },
  {
    value: "oauth2_client",
    label: "OAuth2",
    icon: Lock,
    description: "OAuth 2.0 client credentials flow",
  },
  {
    value: "aws_signature",
    label: "AWS Sig V4",
    icon: Cloud,
    description: "AWS Signature Version 4 signing",
  },
  {
    value: "jwt",
    label: "JWT / Service Account",
    icon: FileKey,
    description: "JWT or Google Service Account",
  },
];

export function AuthTab({ formData, onChange }: AuthTabProps) {
  const handleAuthTypeChange = (authType: string) => {
    onChange({
      auth_type: authType,
      auth_config: {}, // Reset auth config when type changes
    });
  };

  const handleAuthConfigChange = (config: Record<string, unknown>) => {
    onChange({
      auth_config: { ...formData.auth_config, ...config },
    });
  };

  const renderAuthForm = () => {
    switch (formData.auth_type) {
      case "api_key":
        return (
          <APIKeyForm
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
          />
        );
      case "bearer_token":
        return (
          <BearerForm
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
          />
        );
      case "basic_auth":
        return (
          <BasicAuthForm
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
          />
        );
      case "oauth2_client":
      case "oauth2_code":
        return (
          <OAuth2Form
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
          />
        );
      case "aws_signature":
        return (
          <AWSSignatureForm
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
            connectionConfig={formData.config}
            onConfigChange={(config) =>
              onChange({ config: { ...formData.config, ...config } })
            }
          />
        );
      case "jwt":
        return (
          <JWTForm
            config={formData.auth_config}
            onChange={handleAuthConfigChange}
            connectionConfig={formData.config}
            onConfigChange={(config) =>
              onChange({ config: { ...formData.config, ...config } })
            }
          />
        );
      case "none":
      default:
        return (
          <div className="rounded-lg border border-white/10 bg-white/5 p-6 text-center">
            <Globe className="mx-auto h-12 w-12 text-white/40" />
            <p className="mt-4 text-white/60">
              No authentication required for this connection.
            </p>
            <p className="mt-2 text-sm text-white/40">
              The API will be called without any authentication headers.
            </p>
          </div>
        );
    }
  };

  if (formData.type === "file_share") {
    return <SMBConfigForm formData={formData} onChange={onChange} />;
  }

  if (formData.type === "database") {
    return <DatabaseConfigForm formData={formData} onChange={onChange} />;
  }

  return (
    <div className="space-y-6">
      {/* Auth Type Selector */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-white/80">
          Authentication Type
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {AUTH_TYPES.map((type) => {
            const Icon = type.icon;
            const isSelected = formData.auth_type === type.value;

            return (
              <div
                key={type.value}
                onClick={() => handleAuthTypeChange(type.value)}
                className={cn(
                  "cursor-pointer rounded-lg border p-3 transition-all",
                  isSelected
                    ? "border-emerald-500/50 bg-emerald-500/10"
                    : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10",
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    className={cn(
                      "h-5 w-5",
                      isSelected ? "text-emerald-400" : "text-white/50",
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isSelected ? "text-emerald-400" : "text-white/80",
                    )}
                  >
                    {type.label}
                  </span>
                </div>
                <p className="mt-1 text-xs text-white/40 line-clamp-2">
                  {type.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Auth Configuration Form */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-6">
        {renderAuthForm()}
      </div>
    </div>
  );
}
